import React from "react";
import { X, Calendar, Clock, MapPin } from "lucide-react";

/**
 * Lightweight odds modal placeholder for NBA.
 * Mirrors the structure of NCAAFOddsModal: dark overlay, centered card, close button.
 * Currently shows game context; hook up real odds data when available.
 */
const NBAOddsModal = ({ game, onClose, isOpen = true }) => {
  if (!isOpen || !game) return null;

  const { teams, date, arena } = game;
  const home = teams?.home;
  const away = teams?.visitors;
  const start = date?.start ? new Date(date.start) : null;

  const formattedTime = start
    ? start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/New_York"
      }) + " ET"
    : "TBD";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="relative w-full max-w-3xl bg-[#0d0b1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/50">Betting Odds</p>
            <p className="text-lg font-semibold text-white">NBA Game</p>
          </div>
          <button
            aria-label="Close odds modal"
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-4">
            {away?.logo && (
              <img src={away.logo} alt={away.name} className="w-12 h-12 bg-white/5 rounded border border-white/10" />
            )}
            <div className="flex-1">
              <p className="text-sm text-white/70">{away?.name || "Away"}</p>
              <p className="text-xs text-white/40">Visitor</p>
            </div>
            <div className="text-white/50 font-semibold">@</div>
            <div className="flex-1 text-right">
              <p className="text-sm text-white/70">{home?.name || "Home"}</p>
              <p className="text-xs text-white/40">Home</p>
            </div>
            {home?.logo && (
              <img src={home.logo} alt={home.name} className="w-12 h-12 bg-white/5 rounded border border-white/10" />
            )}
          </div>

          <div className="flex items-center gap-4 text-xs text-white/60">
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <Calendar size={14} /> {start ? start.toLocaleDateString() : "Date TBD"}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <Clock size={14} /> {formattedTime}
            </span>
            {arena?.name && (
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <MapPin size={14} /> {arena.name}
              </span>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 text-center">
            <p className="text-white font-semibold text-sm mb-1">Odds coming soon</p>
            <p className="text-white/60 text-xs">
              We’re wiring NBA odds. Check back shortly for live and pre‑match lines.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NBAOddsModal;
